import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '../../design-system/primitives/index.js'
import {
  CUISINE_OPTIONS,
  HEALTHINESS_OPTIONS,
  TIME_OPTIONS,
} from './cookFilterConstants.js'
import { buildCookFilterPendingSummary } from './buildCookFilterPendingSummary.js'

/**
 * Mobile refine filter sheet — desktop-style option lists with pending state until Apply.
 */
export default function CookFilterModal({
  open,
  onClose,
  onApply,
  appliedCuisines = [],
  appliedHealthTiers = [],
  appliedMaxTime = null,
  searchTerm = '',
  showFavoritesOnly = false,
  isAuthenticated = false,
  filterOptions = null,
  filterCount = null,
  filterCountLoading = false,
  onFetchFilterCount,
  onFetchFilterOptions,
  getCountForOption,
}) {
  const [pendingCuisines, setPendingCuisines] = useState([])
  const [pendingHealthTiers, setPendingHealthTiers] = useState([])
  const [pendingMaxTime, setPendingMaxTime] = useState(null)
  const pendingDebounceRef = useRef(null)

  const hasPendingFilters =
    pendingCuisines.length > 0 || pendingHealthTiers.length > 0 || pendingMaxTime != null

  const pendingSummary = useMemo(
    () => buildCookFilterPendingSummary({ pendingMaxTime, pendingCuisines, pendingHealthTiers }),
    [pendingMaxTime, pendingCuisines, pendingHealthTiers],
  )

  const filterApplyLabel = filterCountLoading
    ? 'Apply filters'
    : filterCount === 0
      ? 'Show results'
      : filterCount === 1
        ? 'Show 1 recipe'
        : `Show ${filterCount} recipes`

  function buildFetchOpts(cuisines, healthTiers, maxTime) {
    return {
      search: searchTerm,
      favorites: showFavoritesOnly,
      cuisines,
      healthTiers,
      maxTime,
    }
  }

  useEffect(() => {
    if (!open || !isAuthenticated) return
    setPendingCuisines(appliedCuisines.slice())
    setPendingHealthTiers(appliedHealthTiers.slice())
    setPendingMaxTime(appliedMaxTime)
    const opts = buildFetchOpts(appliedCuisines, appliedHealthTiers, appliedMaxTime)
    onFetchFilterCount?.(opts)
    onFetchFilterOptions?.(opts)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open || !isAuthenticated) return
    if (pendingDebounceRef.current) clearTimeout(pendingDebounceRef.current)
    const id = setTimeout(() => {
      onFetchFilterCount?.(buildFetchOpts(pendingCuisines, pendingHealthTiers, pendingMaxTime))
      onFetchFilterOptions?.(buildFetchOpts(pendingCuisines, pendingHealthTiers, pendingMaxTime))
    }, 300)
    pendingDebounceRef.current = id
    return () => {
      clearTimeout(id)
      pendingDebounceRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingCuisines, pendingHealthTiers, pendingMaxTime])

  function clearPendingFilters() {
    setPendingCuisines([])
    setPendingHealthTiers([])
    setPendingMaxTime(null)
  }

  function handleApply() {
    onApply?.({
      cuisines: pendingCuisines.slice(),
      healthTiers: pendingHealthTiers.slice(),
      maxTime: pendingMaxTime,
    })
    onClose?.()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      titleId="filter-modal-title"
      describedById={pendingSummary ? 'filter-modal-summary' : undefined}
      overlayClassName="modal-overlay--elevated cook-filter-modal-overlay"
      contentClassName="cook-filter-modal-content"
    >
      <ModalHeader className="cook-filter-modal-header">
        <div className="cook-filter-modal-header-text">
          <h2 id="filter-modal-title" className="cook-filter-modal-title">Refine recipes</h2>
          {pendingSummary ? (
            <p id="filter-modal-summary" className="cook-filter-modal-summary">
              {pendingSummary}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          className="modal-close cook-filter-modal-close-btn"
          onClick={onClose}
          aria-label="Close filters"
        >
          ×
        </Button>
      </ModalHeader>
      <ModalBody className="cook-filter-modal-body">
        <section className="cook-filter-section">
          <h3 className="cook-filter-section-title">Cooking / prep time</h3>
          <div
            role="listbox"
            className="cook-filter-menu cook-filter-menu--compact cook-filter-modal-menu"
            aria-label="Cooking time"
          >
            {TIME_OPTIONS
              .filter((opt) => pendingMaxTime === opt.value || getCountForOption?.('time_options', opt.value) !== 0)
              .map((opt) => {
                const isSelected = pendingMaxTime === opt.value
                return (
                  <div
                    key={opt.label}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => setPendingMaxTime(isSelected ? null : opt.value)}
                    className="cook-filter-option"
                  >
                    {opt.label}
                  </div>
                )
              })}
          </div>
        </section>
        <section className="cook-filter-section">
          <h3 className="cook-filter-section-title">Cuisine</h3>
          <div role="listbox" className="cook-filter-menu cook-filter-modal-menu" aria-label="Cuisine">
            {(() => {
              const options = filterOptions?.cuisines?.length
                ? filterOptions.cuisines.map((o) => o.value).filter(Boolean)
                : CUISINE_OPTIONS
              const visible = options.filter(
                (c) => pendingCuisines.includes(c) || getCountForOption?.('cuisines', c) !== 0,
              )
              if (visible.length === 0) {
                return <p className="cook-filter-menu-empty">No cuisines match your recipes.</p>
              }
              return (
                <>
                  {visible.map((cuisine) => {
                    const isSelected = pendingCuisines.includes(cuisine)
                    return (
                      <div
                        key={cuisine}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => {
                          if (isSelected) setPendingCuisines(pendingCuisines.filter((c) => c !== cuisine))
                          else setPendingCuisines([...pendingCuisines, cuisine])
                        }}
                        className="cook-filter-option"
                      >
                        <span>{cuisine}</span>
                      </div>
                    )
                  })}
                  {pendingCuisines.length > 0 && (
                    <>
                      <div className="cook-filter-menu-divider" />
                      <button
                        type="button"
                        role="option"
                        onClick={() => setPendingCuisines([])}
                        className="cook-filter-clear-btn"
                      >
                        Clear all
                      </button>
                    </>
                  )}
                </>
              )
            })()}
          </div>
        </section>
        <section className="cook-filter-section">
          <h3 className="cook-filter-section-title">Diet</h3>
          <div role="listbox" className="cook-filter-menu cook-filter-modal-menu" aria-label="Diet">
            {HEALTHINESS_OPTIONS
              .filter((opt) => pendingHealthTiers.includes(opt.slug) || getCountForOption?.('health_tiers', opt.slug) !== 0)
              .map((opt) => {
                const isSelected = pendingHealthTiers.includes(opt.slug)
                return (
                  <div
                    key={opt.slug}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      if (isSelected) setPendingHealthTiers(pendingHealthTiers.filter((s) => s !== opt.slug))
                      else setPendingHealthTiers([...pendingHealthTiers, opt.slug])
                    }}
                    className="cook-filter-option cook-filter-option--with-icon"
                  >
                    <span aria-hidden="true">
                      {opt.hearts === 1 ? '🍟' : opt.hearts === 2 ? '💚' : '🥗'}
                    </span>
                    <span>{opt.label}</span>
                  </div>
                )
              })}
            {pendingHealthTiers.length > 0 && (
              <>
                <div className="cook-filter-menu-divider" />
                <button
                  type="button"
                  role="option"
                  onClick={() => setPendingHealthTiers([])}
                  className="cook-filter-clear-btn"
                >
                  Clear all
                </button>
              </>
            )}
          </div>
        </section>
      </ModalBody>
      <ModalFooter className="cook-filter-modal-footer">
        <p className="cook-filter-match-summary" aria-live="polite">
          {filterCountLoading ? (
            'Checking…'
          ) : filterCount !== null ? (
            filterCount === 0 ? (
              'No recipes match these filters.'
            ) : (
              <>
                <strong>{filterCount}</strong> {filterCount === 1 ? 'recipe matches' : 'recipes match'}
              </>
            )
          ) : null}
        </p>
        <div className="cook-filter-modal-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={clearPendingFilters}
            disabled={!hasPendingFilters}
            className="cook-filter-modal-reset-btn"
          >
            Clear all
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleApply}
            className="cook-filter-apply-btn"
          >
            {filterApplyLabel}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
